import { EditOutlined } from '@mui/icons-material';
import * as Sentry from '@sentry/react';
import { Image, Skeleton, Typography } from 'antd';
import SpinLoader from 'components/Loader/SpinLoader';
import StoryAudioReader from 'components/StoryWithAi/StoryReader';
import EditModal from 'components/UI/admin/editModal';
import { useAlert } from 'components/UI/alertSakeBar/alertProvider';
import React, { forwardRef, useCallback, useEffect, useRef, useState } from 'react';
import { useDispatch } from 'react-redux';
import { handleUpdateArticleLevel, updateArticleText } from 'store/StoryAiCofig/thunks';
import styled from 'styled-components';
import EditableImage from './ImageViewer';
import { useChapterArticleLevel } from 'store/StoryAiCofig/selectors';
const { Title, Paragraph } = Typography;

// Parent Container
const Container = styled.div`
  background-color: #341b55;
  color: white;
  padding: 10px 10px 10px 10px;
  border-radius: 15px;
  text-align: center;
  position: relative;

  @media print {
    background-color: white !important;
    color: black !important;
    padding: 35px !important; 
    margin: 0;
    border: none;
    width: 100%;
    height: auto;
    overflow: visible;
    border-radius: 0px;
    page-break-before: auto;
    page-break-after: auto;

    .lesson-plan-container {
      page-break-inside: avoid;
    }
  }
`;

const StyledTitle = styled(Title)`
  color: white !important;
  font-weight: bold;
  text-align: center;
  margin-bottom: 2px !important;
  max-width:85%;
  margin:0 auto;
  @media Screen and (max-width: 600px){
    font-size:20px !important;
  }
  @media Screen and (min-width:801px) and (max-width: 830px){
    font-size: 20px !important;
  }
  @media print {
    color: black !important;
    font-size: 22px;
    margin-bottom: 10px !important;
  }
`;

const ContentWrapper = styled.div`
  max-width: 800px;
  margin: 0 auto;
  padding: 12px;
  max-height:60vh;
  overflow-y: auto;
  width: 100%;
  font-size: 18px;
  border-top: 0.5px dashed lightgray;
  @media print {
    border-top: 0 !important;
    max-height: unset;
    overflow: visible;
    height: auto;
    padding: 0;
    margin: 0;
    font-size: 16px;

    & > div {
      page-break-inside: auto !important;
      break-inside: auto !important;
    }
  }
`;
const StyledHtml = styled.span`
  b {
    text-decoration: underline;
  }
`;
const StyledParagraph = styled(Paragraph)`
  color: white;
  text-align: left;
  line-height: 1.8;
  font-size: 18px;
  @media Screen and (max-width: 600px){
    font-size:14px !important;
  }
  @media print {
    color: black !important;
    line-height: 1.5;
    font-size: 16px;
    margin-bottom: 8px;
  }
`;

const StyledImage = styled(Image)`
  border-radius: 18px;
  margin: 0px 0 10px 0;
  max-width: 100%;
  border: 6px solid black;
  box-shadow: 0 4px 10px rgba(0, 0, 0, 0.2); /* Adds shadow around the image */

  @media print {
    max-width: 75%;
    border: 2px solid #000;
    margin-bottom: 10px;
    box-shadow: none; /* Remove shadow for print */
  }
`;


const PrintContainerHidden = styled.div`
  @media print {
    visibility: hidden !important;
    display: none !important;
  }
`;

const PrintBlock = styled.div`
  @media print {
    page-break-inside: auto !important;
    break-inside: auto !important;
  }
`;

const ImageSkeleton = styled(Skeleton.Image)`
    height: 250px !important;
    width: 100% !important;
    border: 2px solid black;
    border-radius: 18px;
`;

const mapWord = (paragraphs,timestampData) => {
  let int_index = 0;
  let global_index = 0;
  return paragraphs.map((element) => {
    const words = element.split(' ');
    const modifiedWords = words.map((word,word_index) => {
     const item =  timestampData[global_index];
     const item_index = global_index;
     if(item){
      if (item.value === word) {
         global_index++;
      }else if(item.value&&item.value.includes(word)){
         const arr = item.value.split(" ");
         if(arr.length==1){
          global_index++;
         }
        else if(arr[arr.length - 1]==word){
          global_index++;
        }
      }else{
        global_index++;
      }
     }
      const currentIndex = ++int_index;
      return `<span style="cursor:pointer;" data-start="${currentIndex}" data-index="${item_index}" class="story_word ${item_index}-${word}"   id="${item_index}_word">${word}</span>`;
    });
    return modifiedWords.join(' ');
  });

}
const ArticleViewAdmin = forwardRef(({ text, images, title, storyInfo, updateStoryInfo }, ref) => {
  const showAlert = useAlert();
  const article_levels = useChapterArticleLevel();
  const [timestampData, setTimestampData] = useState([]);
  const [audioVersion, setAudioVersion] = useState('female');
  const paragraphs = text
  ? mapWord(text.split('\n').filter(Boolean), timestampData)
  : [];
  const last_index = useRef(0)
  const audioRef = useRef()
  const dispatch = useDispatch()
  const contentWrapperRef = useRef(null);
  const [image_update_loader, setImage_update_loader] = useState(false);
  const [editModal, setEditModal] = useState(false);
  const [editText, setEditText] = useState(text);
  const [editLoader, setEditLoader] = useState(false);

  useEffect(() => {
    setEditText(text);
  }, [storyInfo])

  function getTimeStamp(text) {
    try {
      const value = JSON.parse(text);
      if (Array.isArray(value)) {
        return value;
      }
      const lines = text.trim().split('\n').map((line) => JSON.parse(line));
      return lines;
    } catch (error) {
      const lines = text.trim().split('\n').map((line) => JSON.parse(line));
      return lines;
    }
  }
  useEffect(() => {
    const loadTimestamps = async () => {
      const url =
        audioVersion === 'male'
          ? storyInfo?.male_audio_timestamp
          : storyInfo?.female_audio_timestamp;

      if (!url) return setTimestampData([]);

      try {
        const res = await fetch(url);
        const text = await res.text();
        const lines = getTimeStamp(text);
        setTimestampData(lines);
      } catch (err) {
        Sentry.captureException(`312 Error loading timestamp JSON: ${err}`);
        console.error('Error loading timestamp JSON:', err);
        setTimestampData([]);
      }
    };
    loadTimestamps();
  }, [storyInfo, audioVersion]);
  const handleAudioProgress = (time) => {
    let currentIndex = -1;
    currentIndex = timestampData?.findIndex((entry, idx) => entry.time <= time && (idx === timestampData.length - 1 || timestampData[idx + 1].time > time));
    if (currentIndex === -1 || currentIndex === last_index.current) return;
    document.querySelectorAll('.story_word.highlighted').forEach(el => {
      el.classList.remove('highlighted');
    });

    const item = document.getElementById(`${currentIndex}_word`);
    last_index.current = currentIndex;

    if (item) {
      item.classList.add('highlighted');
      if (contentWrapperRef.current) {
        const container = contentWrapperRef.current;
        const containerTop = container.getBoundingClientRect().top;
        const itemTop = item.getBoundingClientRect().top;
        const offset = itemTop - containerTop;
  //comment scroll for now
        // container.scrollTo({
        //   top: offset + container.scrollTop - container.clientHeight / 2,
        //   behavior: 'smooth',
        // });
      }
    }
  };
  useEffect(() => {
    const handleClick = (e) => {
      const wordElement = e.target.closest('.story_word');
      if(!wordElement) return;
      // if (!e.target.classList.contains('story_word')) return;
      const index = parseInt(wordElement.getAttribute('data-index'), 10);
      if (timestampData[index] && audioRef.current?.setTime) {
        const time = timestampData[index].time;
        audioRef.current.setTime(time);
      }
    };
  
    const container = contentWrapperRef.current;
    if (container) {
      container.addEventListener('click', handleClick);
    }
  
    return () => {
      if (container) {
        container.removeEventListener('click', handleClick);
      }
    };
  }, [timestampData]);
  const onUploadImage = async({ url, index }) => {
    setImage_update_loader(true)
    try {
      const data = await  dispatch(handleUpdateArticleLevel({
        id: storyInfo.id,
        url,
        index
      }))
      if (data && data.success) {
        showAlert("Image updated successfully","success")
      } else {
        showAlert("Failed to update image","error")
      }
    } catch (error) {
      Sentry.captureException(`313 Error updating image: ${error}`);
      showAlert("Something went wrong!","error")
    } finally {
      setImage_update_loader(false)
    }
  }
  const SkeltonImage = (index) => {
    if (index <= 2) {
      return <ImageSkeleton active />
    }
    return null
  }
    const getImages = useCallback((index) => {
      if (images.length == 0) return SkeltonImage(index)
        const arr  = text.split('\n').filter(Boolean)
      return images[index] ? (
        <EditableImage
        key={`editable-img-${index}`}
        index={index}
        src={images[index]}
        onImageChange={onUploadImage}
        story_pragraph={arr?arr[index]:''}
      />
      ) : images.length > 0 ? null : SkeltonImage(index)
    }, [images,text])


    const handlerEdit = async () => {
      setEditLoader(true);
      try {
        const payload = storyInfo;
        payload.articles_text = editText;
        const res = await updateArticleText({
          id: storyInfo?.id,
          articles_text: editText,
        });
        await updateStoryInfo(payload,res);
        setTimeout(()=>{
          setEditLoader(false);
          setEditModal(false);
        },5000)
      } catch (error) {
        Sentry.captureException(`314 Error updating article text: ${error}`);
        console.log(error);
      }
    };

    const handlerOpenEdit = () => {
      setEditModal(true);
    }

  return (
    <Container className="to_be_print printable" ref={ref}>
        <div className='w-full justify-end flex items-center'>
        <span className='mb-10px'>
          <EditOutlined className='cursor-pointer' onClick={handlerOpenEdit}>edit</EditOutlined>
        </span>
      </div>
      <StyledTitle level={2}>{title || ''}</StyledTitle>
       <SpinLoader dataloader={image_update_loader} />
      {/* Audio reader (hidden in print) */}
      <PrintContainerHidden>
        <StoryAudioReader
          id={storyInfo?.id}
          ref={audioRef}
          male_audio={storyInfo?.male_audio}
          female_audio={storyInfo?.female_audio}
          player={true}
          onTimeUpdate={handleAudioProgress}
          onVoiceChange={(version) => {
            last_index.current = 0;
            setAudioVersion(version)
          }}
        />
      </PrintContainerHidden>
      <EditModal
        key={storyInfo?.id}
        open={editModal}
        onClose={() => setEditModal(false)} 
        handlerSave={handlerEdit} 
        loading={editLoader} 
        content={editText}
        setContent={setEditText}
    />
      <ContentWrapper   ref={contentWrapperRef}>
        {paragraphs.map((para, index) => (
          <PrintBlock key={index}>
            <StyledParagraph>
              <StyledHtml dangerouslySetInnerHTML={{ __html: para }} />
            </StyledParagraph>
            {/* Display image only if available */}
            {getImages(index)}
          </PrintBlock>
        ))}
      </ContentWrapper>
    </Container>
  );
});

export default ArticleViewAdmin;